import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import UserDashboard from "./pages/UserDashboard";
import ApplicationForm from "./pages/ApplicationForm";
import BankInfoPage from "./pages/BankInfoPage";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminApplicationDetail from "./pages/AdminApplicationDetail";
import AdminSiteEditor from "./pages/AdminSiteEditor";
import RateCardPage from "./pages/RateCardPage";

const LegacyRateCardRedirect = () => <Navigate to={`/rate-card/${useParams().token}`} replace />;
import { AdminHomeButton } from "./components/AdminHomeButton";
import "./App.css";

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0F]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to={requireAdmin ? "/admin/login" : "/login"} state={{ from: location.pathname }} replace />;
  }
  
  if (requireAdmin && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" offset={72} mobileOffset={{ top: 72 }} richColors closeButton />
        <AdminHomeButton />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/rate-card/:token" element={<RateCardPage />} />
          <Route path="/rates/:token" element={<LegacyRateCardRedirect />} />
          
          {/* User Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <UserDashboard />
            </ProtectedRoute>
          } />
          <Route path="/apply" element={
            <ProtectedRoute>
              <ApplicationForm />
            </ProtectedRoute>
          } />
          <Route path="/bank-info" element={
            <ProtectedRoute>
              <BankInfoPage />
            </ProtectedRoute>
          } />
          
          {/* Admin Protected Routes */}
          <Route path="/admin" element={
            <ProtectedRoute requireAdmin>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/applications/:id" element={
            <ProtectedRoute requireAdmin>
              <AdminApplicationDetail />
            </ProtectedRoute>
          } />
          <Route path="/admin/site-editor" element={
            <ProtectedRoute requireAdmin>
              <AdminSiteEditor />
            </ProtectedRoute>
          } />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
